import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "./Toast";

type TypedConfirmation = {
  value: string;
  label?: string;
  copyable?: boolean;
};

/** ADR-0111: a non-cancel button for the tri-state `confirmChoice` variant. */
type ChoiceButton = { label: string; value: string; destructive?: boolean; primary?: boolean };

/**
 * ADR-0181: a required free-text field inside the confirmation.
 *
 * Distinct from `requireTypedConfirmation`, which asks the user to echo a
 * known string as a speed bump. This asks them to SAY SOMETHING that is then
 * kept: the purge reason is stored on each purged version's record and served
 * back to anyone who reads it. The dialog is the right place for it because
 * the reason is part of the decision, not a follow-up to it.
 */
type ReasonField = {
  label: string;
  placeholder?: string;
  maxLength?: number;
};

type ConfirmOpts = {
  title: string;
  detail?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  requireTypedConfirmation?: TypedConfirmation;
  /** ADR-0111: when set, render these buttons instead of the binary Confirm/Cancel pair. */
  choices?: ChoiceButton[];
  /** ADR-0181: a required free-text reason, returned by `confirmWithReason`. */
  reasonField?: ReasonField;
};

type ConfirmCtx = {
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
  /**
   * ADR-0111: a 3+-way prompt (e.g. Save / Discard / Cancel). Resolves to the
   * chosen button's `value`, or `null` if dismissed (Cancel / overlay / Escape).
   * Reuses the same modal shell as `confirm`.
   */
  confirmChoice: (opts: { title: string; detail?: string; choices: ChoiceButton[]; cancelLabel?: string }) => Promise<string | null>;
  /**
   * ADR-0181: confirm WITH a required reason. Resolves to the trimmed reason,
   * or `null` if dismissed. Confirm stays disabled until the field has content,
   * so an empty reason refuses inline rather than through a toast after the
   * fact (UI_GUIDELINES §2.9 — validation errors are not toasts).
   */
  confirmWithReason: (
    opts: Omit<ConfirmOpts, "choices" | "requireTypedConfirmation"> & { reasonField: ReasonField }
  ) => Promise<string | null>;
};

const Ctx = createContext<ConfirmCtx | null>(null);

export function useConfirm() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return ctx;
}

export function ConfirmProvider(props: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOpts | null>(null);
  const [resolver, setResolver] = useState<((v: string | null) => void) | null>(null);
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const reasonRef = useRef<HTMLInputElement | null>(null);

  const confirm = useCallback((o: ConfirmOpts) => {
    setOpts(o);
    setTyped("");
    setOpen(true);
    // Internal outcome is a string; "__confirm__" maps to true, anything else (null) to false.
    return new Promise<boolean>((resolve) => setResolver(() => (v: string | null) => resolve(v === "__confirm__")));
  }, []);

  const confirmChoice = useCallback((o: { title: string; detail?: string; choices: ChoiceButton[]; cancelLabel?: string }) => {
    setOpts({ title: o.title, detail: o.detail, choices: o.choices, cancelLabel: o.cancelLabel });
    setTyped("");
    setOpen(true);
    return new Promise<string | null>((resolve) => setResolver(() => (v: string | null) => resolve(v)));
  }, []);

  const confirmWithReason = useCallback(
    (o: Omit<ConfirmOpts, "choices" | "requireTypedConfirmation"> & { reasonField: ReasonField }) => {
      setOpts(o);
      setTyped("");
      setReason("");
      setOpen(true);
      // The resolver receives the reason text itself, so the caller never has
      // to reach back into the dialog for it.
      return new Promise<string | null>((resolve) => setResolver(() => (v: string | null) => resolve(v)));
    },
    []
  );

  const close = useCallback((result: string | null) => {
    setOpen(false);
    resolver?.(result);
    setResolver(null);
    setOpts(null);
    setTyped("");
    setReason("");
  }, [resolver]);

  const value = useMemo(
    () => ({ confirm, confirmChoice, confirmWithReason }),
    [confirm, confirmChoice, confirmWithReason]
  );

  useEffect(() => {
    if (open && opts?.requireTypedConfirmation) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    if (open && opts?.reasonField) {
      const id = window.setTimeout(() => reasonRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open, opts]);

  const required = opts?.requireTypedConfirmation?.value ?? null;
  const typedMatches = required === null ? true : typed === required;
  const reasonRequired = opts?.reasonField != null;
  const confirmDisabled = (required !== null && !typedMatches) || (reasonRequired && reason.trim().length === 0);
  const confirmClass = opts?.destructive ? "btn btnDanger" : "btn btnPrimary";

  const handleCopy = useCallback(async (value: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.push({ kind: "success", message: "Copied." });
    } catch {
      toast.push({ kind: "error", message: "Copy failed." });
    }
  }, [toast]);

  return (
    <Ctx.Provider value={value}>
      {props.children}
      {open && opts && (
        <div className="modalOverlay" role="presentation" onClick={() => close(null)}>
          <div className="modal modalScrollable" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="modalScrollHeader">
              <div style={{ fontWeight: 800 }}>{opts.title}</div>
            </div>
            <div className="modalScrollBody" style={{ paddingTop: 6, paddingBottom: 12 }}>
            {opts.detail ? <div className="muted" style={{ marginBottom: 12 }}>{opts.detail}</div> : null}
            {opts.requireTypedConfirmation ? (
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 13 }}>
                  {opts.requireTypedConfirmation.label ?? "Type the name to confirm:"}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <code
                    className="mono"
                    style={{
                      padding: "2px 6px",
                      background: "var(--surface-2, rgba(0,0,0,0.05))",
                      borderRadius: 4,
                      fontSize: 13,
                      userSelect: "all"
                    }}
                  >
                    {opts.requireTypedConfirmation.value}
                  </code>
                  {opts.requireTypedConfirmation.copyable !== false ? (
                    <button
                      type="button"
                      className="btn btnSm"
                      aria-label="Copy name"
                      title="Copy"
                      onClick={() => void handleCopy(opts.requireTypedConfirmation!.value)}
                      style={{ padding: "2px 6px", lineHeight: 1 }}
                    >
                      ⧉
                    </button>
                  ) : null}
                </div>
                <input
                  ref={inputRef}
                  className="input"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !confirmDisabled) close("__confirm__");
                    if (e.key === "Escape") close(null);
                  }}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            ) : null}
            {opts.reasonField ? (
              <label className="field">
                <span className="label">{opts.reasonField.label}</span>
                <input
                  ref={reasonRef}
                  className="input"
                  value={reason}
                  placeholder={opts.reasonField.placeholder}
                  maxLength={opts.reasonField.maxLength ?? 500}
                  onChange={(e) => setReason(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !confirmDisabled) close(reason.trim());
                    if (e.key === "Escape") close(null);
                  }}
                  autoComplete="off"
                />
              </label>
            ) : null}
            </div>
            <div className="modalScrollFooter" style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn" onClick={() => close(null)}>
                {opts.cancelLabel ?? "Cancel"}
              </button>
              {opts.choices ? (
                opts.choices.map((c) => (
                  <button
                    key={c.value}
                    className={c.destructive ? "btn btnDanger" : c.primary ? "btn btnPrimary" : "btn"}
                    onClick={() => close(c.value)}
                  >
                    {c.label}
                  </button>
                ))
              ) : (
                <button
                  className={confirmClass}
                  onClick={() => close(opts.reasonField ? reason.trim() : "__confirm__")}
                  disabled={confirmDisabled}
                >
                  {opts.confirmLabel ?? "Confirm"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

